import * as React from 'react'
import { SavedImage } from './useSaveImage'

interface UseUnsavedChangesProps {
	pendingAction: string | null
	pendingImageSelection: SavedImage | null
	setPendingImageSelection: (image: SavedImage | null) => void
	handleSaveChanges: () => void
	handleDiscardChanges: () => void
	handleImageSelect: (image: SavedImage) => void
	handleGenerateWithDialog: () => Promise<void>
}

interface UseUnsavedChangesResult {
	extendedHandleSaveChanges: () => void
	extendedHandleDiscardChanges: () => void
	wrappedHandleGenerate: () => Promise<void>
}

export const useUnsavedChanges = ({
	pendingAction,
	pendingImageSelection,
	setPendingImageSelection,
	handleSaveChanges,
	handleDiscardChanges,
	handleImageSelect,
	handleGenerateWithDialog,
}: UseUnsavedChangesProps): UseUnsavedChangesResult => {
	// Extend handleSaveChanges to handle image selection
	const extendedHandleSaveChanges = React.useCallback(() => {
		handleSaveChanges()

		// If we were trying to select a new image, do it after saving
		if (pendingAction === 'selectImage' && pendingImageSelection) {
			handleImageSelect(pendingImageSelection)
			setPendingImageSelection(null)
		}
	}, [handleSaveChanges, pendingAction, pendingImageSelection, handleImageSelect, setPendingImageSelection])

	// Extend handleDiscardChanges to handle image selection
	const extendedHandleDiscardChanges = React.useCallback(() => {
		handleDiscardChanges()

		// If we were trying to select a new image, do it after discarding
		if (pendingAction === 'selectImage' && pendingImageSelection) {
			handleImageSelect(pendingImageSelection)
			setPendingImageSelection(null)
		}
	}, [handleDiscardChanges, pendingAction, pendingImageSelection, handleImageSelect, setPendingImageSelection])

	// Wrap the original handleGenerate function
	const wrappedHandleGenerate = React.useCallback(() => {
		return handleGenerateWithDialog()
	}, [handleGenerateWithDialog])

	return {
		extendedHandleSaveChanges,
		extendedHandleDiscardChanges,
		wrappedHandleGenerate,
	}
}
